import { useEffect, useState } from "react";
import {
  ArrowUpRight,
  BadgeAlert,
  BrainCircuit,
  ChevronRight,
  FileWarning,
  Landmark,
  ScanSearch,
  Scale,
  ShieldAlert,
  Users,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";

const problems = [
  {
    slug: "credito",
    title: "Scoring opaco",
    description:
      "Prestiti, assicurazioni o servizi vengono negati senza una spiegazione chiara e verificabile.",
    label: "Finanza & accesso",
    Icon: Landmark,
  },
  {
    slug: "lavoro",
    title: "Selezioni distorte",
    description:
      "CV filtrati male, profili esclusi e valutazioni automatizzate che amplificano bias già esistenti.",
    label: "Lavoro & opportunità",
    Icon: Users,
  },
  {
    slug: "frodi",
    title: "Deepfake e truffe",
    description:
      "Volti, voci e identità sintetiche usate per raggiri, reputazione danneggiata e confusione informativa.",
    label: "Sicurezza personale",
    Icon: ShieldAlert,
  },
  {
    slug: "servizi",
    title: "Assistenza automatica che sbaglia",
    description:
      "Chatbot, sistemi di pricing e filtri automatici che prendono decisioni ingestibili per il cittadino.",
    label: "Consumo digitale",
    Icon: BrainCircuit,
  },
];

const interventions = [
  {
    slug: "segnalazione",
    step: "01",
    title: "Raccogliamo il caso",
    description:
      "Definiamo il tipo di danno, i soggetti coinvolti e i documenti minimi necessari per contestare l'errore.",
    Icon: FileWarning,
  },
  {
    slug: "analisi",
    step: "02",
    title: "Traduciamo l'algoritmo in diritti",
    description:
      "Valutiamo trasparenza, discriminazione, decisione automatizzata e correttezza commerciale in chiave italiana ed europea.",
    Icon: ScanSearch,
  },
  {
    slug: "azione",
    step: "03",
    title: "Attiviamo pressione e tutela",
    description:
      "Orientamento individuale, segnalazione collettiva, advocacy pubblica e materiali pronti da usare per reagire.",
    Icon: Scale,
  },
];

const faqItems = [
  {
    slug: "chi",
    question: "Quando un errore dell'AI diventa un problema di tutela del consumatore?",
    answer:
      "Quando una decisione automatizzata incide in modo concreto sui tuoi diritti: accesso a servizi, prezzo, reputazione, opportunità lavorative o trattamento dei dati. Il punto non è la tecnologia in sé, ma il danno che produce e l'assenza di rimedi chiari.",
  },
  {
    slug: "prove",
    question: "Che cosa serve per iniziare una segnalazione?",
    answer:
      "Schermate, email, contratti, messaggi di rifiuto, registrazioni o qualsiasi traccia che colleghi la decisione a un sistema automatizzato. Anche con pochi elementi possiamo aiutarti a capire quali prove rafforzare.",
  },
  {
    slug: "aziende",
    question: "Vi occupate solo di big tech?",
    answer:
      "No. Interveniamo ogni volta che un sistema AI o algoritmico tocca il cittadino: banche, assicurazioni, piattaforme, e-commerce, recruiting, customer care, utility e pubblica interazione digitale.",
  },
  {
    slug: "obiettivo",
    question: "L'associazione vuole bloccare l'innovazione?",
    answer:
      "No. L'obiettivo è pretendere innovazione responsabile. Chi usa l'AI in Italia deve spiegare, correggere, rendere contestabili gli errori e assumersi la responsabilità delle conseguenze.",
  },
];

const signalStrip = [
  "Prezzi dinamici incomprensibili",
  "Mutui negati da scoring opachi",
  "Recruiting automatizzato distorto",
  "Deepfake e impersonificazioni",
];

const infoBadges = ["GDPR", "AI Act", "Codice del Consumo"];

const initialForm = {
  fullName: "",
  email: "",
  issueType: "",
  impact: "",
  details: "",
};

export default function LandingPage() {
  const [formData, setFormData] = useState(initialForm);
  const [savedDraft, setSavedDraft] = useState(null);

  useEffect(() => {
    document.title = "SviluppAi.it";
  }, []);

  const updateField = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const payload = {
      ...formData,
      savedAt: new Date().toISOString(),
    };

    window.localStorage.setItem("sviluppai-it-draft", JSON.stringify(payload));

    const fileContent = [
      "Bozza segnalazione — SviluppAi.it",
      `Nome: ${payload.fullName}`,
      `Email: ${payload.email}`,
      `Tipo di caso: ${payload.issueType}`,
      `Impatto percepito: ${payload.impact}`,
      `Dettagli: ${payload.details}`,
      `Salvata il: ${new Date(payload.savedAt).toLocaleString("it-IT")}`,
    ].join("\n");

    const blob = new Blob([fileContent], { type: "text/plain;charset=utf-8" });
    const downloadUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = downloadUrl;
    anchor.download = "bozza-segnalazione-sviluppai.txt";
    anchor.click();
    window.URL.revokeObjectURL(downloadUrl);

    setSavedDraft(payload);
    setFormData(initialForm);
    toast.success("Bozza salvata sul dispositivo e scaricata in formato testo.");
  };

  return (
    <div className="bg-background text-foreground" data-testid="landing-page-root">
      <header
        className="sticky top-0 z-50 border-b-2 border-foreground bg-background/95 backdrop-blur-sm"
        data-testid="site-header"
      >
        <div className="page-container flex flex-wrap items-center justify-between gap-4 py-4">
          <a
            href="#top"
            className="flex items-center gap-3"
            data-testid="header-brand-link"
          >
            <span className="flex h-11 w-11 items-center justify-center border-2 border-foreground bg-primary text-sm font-extrabold uppercase tracking-[0.2em] text-white">
              AI
            </span>
            <div>
              <p
                className="font-display text-lg font-extrabold tracking-tight"
                data-testid="header-brand-name"
              >
                SviluppAi.it
              </p>
              <p className="text-xs uppercase tracking-[0.22em] text-muted" data-testid="header-brand-tagline">
                Associazione italiana a tutela dei consumatori contro gli errori dell'AI
              </p>
            </div>
          </a>

          <nav className="flex flex-wrap items-center gap-2 md:gap-5" data-testid="header-navigation">
            <a href="#missione" className="text-xs font-bold uppercase tracking-[0.2em] transition-colors hover:text-primary" data-testid="nav-missione-link">
              Missione
            </a>
            <a href="#rischi" className="text-xs font-bold uppercase tracking-[0.2em] transition-colors hover:text-primary" data-testid="nav-rischi-link">
              Rischi reali
            </a>
            <a href="#come-funziona" className="text-xs font-bold uppercase tracking-[0.2em] transition-colors hover:text-primary" data-testid="nav-come-funziona-link">
              Come agiamo
            </a>
            <a href="#aderisci" className="button-primary px-5 py-3 text-xs md:px-6" data-testid="nav-aderisci-button">
              Unisciti a noi
            </a>
          </nav>
        </div>
      </header>

      <main id="top">
        <section className="overflow-hidden" data-testid="hero-section">
          <div className="page-container grid gap-10 py-16 md:py-24 lg:grid-cols-[1.1fr_0.9fr] lg:items-end lg:py-28">
            <div className="reveal-up">
              <p className="section-kicker" data-testid="hero-kicker">
                Associazione nazionale di tutela dei consumatori
              </p>
              <h1
                className="max-w-4xl font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl lg:text-6xl"
                data-testid="hero-main-title"
              >
                L'Intelligenza Artificiale ha sbagliato. Il cittadino non deve restare solo.
              </h1>
              <p
                className="mt-6 max-w-2xl text-sm leading-7 text-muted md:text-lg"
                data-testid="hero-main-subtitle"
              >
                Siamo un'associazione che unisce cittadini, famiglie e comunità per difendere i consumatori italiani da decisioni automatizzate ingiuste, errori di scoring, discriminazioni, prezzi opachi e danni causati dall'intelligenza artificiale.
              </p>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="#aderisci" className="button-primary" data-testid="hero-primary-cta-button">
                  Segnala il tuo caso <ChevronRight className="h-4 w-4" />
                </a>
                <a href="#come-funziona" className="button-secondary" data-testid="hero-secondary-cta-button">
                  Come funziona <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>

              <div className="mt-10 grid gap-4 sm:grid-cols-3">
                {infoBadges.map((item) => (
                  <div className="frame-card bg-accent/40 p-5" key={item} data-testid={`hero-legal-badge-${item.toLowerCase().replace(/\s+/g, "-")}`}>
                    <p className="eyebrow-label">Leva di tutela</p>
                    <p className="mt-3 font-display text-xl font-bold tracking-tight">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="reveal-up reveal-delay-2 relative">
              <div className="absolute -left-4 top-10 hidden h-28 w-28 border-2 border-foreground bg-primary md:block" />
              <div className="frame-card relative overflow-hidden p-0" data-testid="hero-visual-card">
                <div className="aspect-[4/5] border-b-2 border-foreground bg-secondary">
                  <img
                    src="https://images.unsplash.com/photo-1748895189778-8234c01c732c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                    alt="Piazza italiana in bianco e nero"
                    className="image-lift h-full w-full object-cover object-center grayscale opacity-80 mix-blend-luminosity"
                    data-testid="hero-image"
                  />
                </div>
                <div className="grid gap-4 p-6 md:grid-cols-[1fr_auto] md:items-end md:p-8">
                  <div>
                    <p className="eyebrow-label" data-testid="hero-visual-label">
                      Focus operativo
                    </p>
                    <p className="mt-3 max-w-md font-display text-2xl font-bold tracking-tight" data-testid="hero-visual-title">
                      Rendere contestabili le decisioni prese da macchine che nessuno spiega.
                    </p>
                  </div>
                  <div className="border-2 border-foreground bg-background px-4 py-3 text-sm font-semibold uppercase tracking-[0.2em]" data-testid="hero-alert-panel">
                    Priorità 2026
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="section-border bg-primary text-white" data-testid="hero-signal-strip">
            <div className="page-container grid gap-3 py-5 md:grid-cols-4">
              {signalStrip.map((item, index) => (
                <div className="flex items-center gap-3" key={item} data-testid={`signal-strip-item-${index + 1}`}>
                  <BadgeAlert className="h-4 w-4 shrink-0" />
                  <span className="text-sm font-semibold uppercase tracking-[0.12em]">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="missione" className="section-border py-24 md:py-32" data-testid="mission-section">
          <div className="page-container grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
            <div className="reveal-up">
              <p className="section-kicker" data-testid="mission-kicker">
                Missione
              </p>
              <h2 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="mission-title">
                Non sei un edge case.
              </h2>
            </div>

            <div className="reveal-up reveal-delay-1 space-y-6">
              <p className="max-w-3xl border-l-4 border-primary pl-5 text-lg leading-8 text-foreground md:text-2xl" data-testid="mission-statement">
                Quando un algoritmo decide male, il danno ricade su persone reali. Noi ci muoviamo insieme per trasformare ogni errore tecnico in una battaglia collettiva per diritti, trasparenza e responsabilità.
              </p>
              <div className="grid gap-5 md:grid-cols-3">
                <article className="frame-card" data-testid="mission-card-trasparenza">
                  <p className="eyebrow-label">Trasparenza</p>
                  <p className="mt-3 font-display text-2xl font-bold tracking-tight">Capire</p>
                  <p className="mt-3 text-sm leading-7 text-muted">
                    Chiedere spiegazioni leggibili sulle logiche automatiche che incidono sulla tua vita.
                  </p>
                </article>
                <article className="frame-card" data-testid="mission-card-contestazione">
                  <p className="eyebrow-label">Contestazione</p>
                  <p className="mt-3 font-display text-2xl font-bold tracking-tight">Reagire</p>
                  <p className="mt-3 text-sm leading-7 text-muted">
                    Documentare l'errore, individuare i profili di illegittimità e rendere la contestazione più forte.
                  </p>
                </article>
                <article className="frame-card" data-testid="mission-card-rappresentanza">
                  <p className="eyebrow-label">Rappresentanza</p>
                  <p className="mt-3 font-display text-2xl font-bold tracking-tight">Fare sistema</p>
                  <p className="mt-3 text-sm leading-7 text-muted">
                    Unire casi singoli per produrre pressione pubblica, standard più chiari e pratiche migliori.
                  </p>
                </article>
              </div>
            </div>
          </div>
        </section>

        <section id="rischi" className="section-border bg-white py-24 md:py-32" data-testid="problems-section">
          <div className="page-container">
            <div className="reveal-up flex flex-col gap-5 lg:max-w-3xl">
              <p className="section-kicker" data-testid="problems-kicker">
                I rischi reali
              </p>
              <h2 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="problems-title">
                L'errore dell'AI non è astratto: arriva nei contratti, nei prezzi, nei rifiuti, nella reputazione.
              </h2>
              <p className="text-sm leading-7 text-muted md:text-lg" data-testid="problems-subtitle">
                Siamo al fianco di chi ha già subito un torto e di chi vuole impedire che l'automazione venga usata senza responsabilità verso i consumatori e i cittadini italiani.
              </p>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              <div className="grid gap-6 lg:col-span-2 md:grid-cols-2">
                {problems.map(({ slug, title, description, label, Icon }) => (
                  <article className="frame-card reveal-up" key={slug} data-testid={`problem-card-${slug}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="eyebrow-label">{label}</p>
                        <h3 className="mt-3 font-display text-2xl font-bold tracking-tight" data-testid={`problem-title-${slug}`}>
                          {title}
                        </h3>
                      </div>
                      <div className="flex h-12 w-12 items-center justify-center border-2 border-foreground bg-background" data-testid={`problem-icon-${slug}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                    </div>
                    <p className="mt-5 text-sm leading-7 text-muted md:text-base" data-testid={`problem-description-${slug}`}>
                      {description}
                    </p>
                  </article>
                ))}
              </div>

              <div className="grid gap-6">
                <article className="frame-card reveal-up reveal-delay-1 overflow-hidden p-0" data-testid="problem-image-card">
                  <div className="aspect-[4/5] border-b-2 border-foreground">
                    <img
                      src="https://images.unsplash.com/photo-1674471361339-2e1e1dbd3e73?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                      alt="Utente frustrato davanti a un computer"
                      className="image-lift h-full w-full object-cover object-center grayscale"
                      data-testid="problem-support-image"
                    />
                  </div>
                  <div className="p-6 md:p-8">
                    <p className="eyebrow-label">Segnale di allarme</p>
                    <p className="mt-3 font-display text-2xl font-bold tracking-tight" data-testid="problem-image-card-title">
                      Se la decisione arriva senza una spiegazione verificabile, il rischio per il consumatore è già iniziato.
                    </p>
                  </div>
                </article>

                <article className="border-2 border-foreground bg-secondary p-6 text-secondary-foreground shadow-[8px_8px_0_0_rgba(0,0,0,0.12)] md:p-8" data-testid="problem-manifesto-card">
                  <p className="eyebrow-label !text-secondary-foreground/70">Posizione pubblica</p>
                  <p className="mt-4 font-display text-2xl font-bold tracking-tight" data-testid="problem-manifesto-title">
                    L'automazione non può diventare un alibi per negare spiegazioni, rimedi e responsabilità.
                  </p>
                  <div className="mt-6 flex items-center gap-3 border-t-2 border-secondary-foreground/20 pt-5 text-sm uppercase tracking-[0.18em]" data-testid="problem-manifesto-footer">
                    <ShieldAlert className="h-4 w-4" />
                    Presidio civico permanente
                  </div>
                </article>
              </div>
            </div>
          </div>
        </section>

        <section id="come-funziona" className="section-border py-24 md:py-32" data-testid="workflow-section">
          <div className="page-container grid gap-10 lg:grid-cols-[1fr_0.8fr]">
            <div>
              <div className="reveal-up lg:max-w-3xl">
                <p className="section-kicker" data-testid="workflow-kicker">
                  Come agiamo
                </p>
                <h2 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="workflow-title">
                  Tre passaggi per trasformare un caso isolato in una risposta concreta.
                </h2>
              </div>

              <div className="mt-10 grid gap-6">
                {interventions.map(({ slug, step, title, description, Icon }, index) => (
                  <article className={`frame-card reveal-up reveal-delay-${Math.min(index + 1, 4)}`} key={slug} data-testid={`workflow-card-${slug}`}>
                    <div className="grid gap-5 md:grid-cols-[auto_1fr_auto] md:items-start">
                      <p className="font-display text-4xl font-extrabold text-primary" data-testid={`workflow-step-${slug}`}>
                        {step}
                      </p>
                      <div>
                        <h3 className="font-display text-2xl font-bold tracking-tight" data-testid={`workflow-title-${slug}`}>
                          {title}
                        </h3>
                        <p className="mt-3 text-sm leading-7 text-muted md:text-base" data-testid={`workflow-description-${slug}`}>
                          {description}
                        </p>
                      </div>
                      <div className="flex h-12 w-12 items-center justify-center border-2 border-foreground bg-accent" data-testid={`workflow-icon-${slug}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <aside className="reveal-up reveal-delay-2 grid gap-6 self-start">
              <article className="frame-card" data-testid="legal-framework-card">
                <p className="section-kicker mb-6" data-testid="legal-framework-kicker">
                  Quadro di riferimento
                </p>
                <ul className="space-y-5" data-testid="legal-framework-list">
                  <li className="border-b-2 border-foreground pb-5">
                    <p className="eyebrow-label">Tutela dati</p>
                    <p className="mt-2 font-display text-2xl font-bold tracking-tight">Diritto a sapere</p>
                    <p className="mt-2 text-sm leading-7 text-muted">Capire se una decisione automatizzata ha usato dati personali in modo scorretto o poco trasparente.</p>
                  </li>
                  <li className="border-b-2 border-foreground pb-5">
                    <p className="eyebrow-label">Tutela mercato</p>
                    <p className="mt-2 font-display text-2xl font-bold tracking-tight">Diritto a contestare</p>
                    <p className="mt-2 text-sm leading-7 text-muted">Contrastare pratiche commerciali scorrette, prezzi opachi e servizi automatici che non offrono rimedi reali.</p>
                  </li>
                  <li>
                    <p className="eyebrow-label">Tutela collettiva</p>
                    <p className="mt-2 font-display text-2xl font-bold tracking-tight">Diritto a farti rappresentare</p>
                    <p className="mt-2 text-sm leading-7 text-muted">Rendere visibile il problema, coordinare casi simili e spingere verso regole e controlli migliori.</p>
                  </li>
                </ul>
              </article>

              <article className="frame-card overflow-hidden p-0" data-testid="community-image-card">
                <div className="aspect-[4/3] border-b-2 border-foreground">
                  <img
                    src="https://images.unsplash.com/photo-1615931917925-824ed7c3e6bf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                    alt="Persone riunite per discutere"
                    className="image-lift h-full w-full object-cover object-center grayscale"
                    data-testid="community-image"
                  />
                </div>
                <div className="p-6 md:p-8">
                  <p className="eyebrow-label">Presenza sul territorio</p>
                  <p className="mt-3 font-display text-2xl font-bold tracking-tight" data-testid="community-image-title">
                    Una voce pubblica, chiara e riconoscibile per chi non vuole essere trattato come un errore statistico.
                  </p>
                </div>
              </article>
            </aside>
          </div>
        </section>

        <section id="aderisci" className="section-border bg-white py-24 md:py-32" data-testid="membership-section">
          <div className="page-container grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
            <div className="reveal-up grid gap-6">
              <div>
              <p className="section-kicker" data-testid="membership-kicker">
                  Aderisci / prepara la segnalazione
                </p>
                <h2 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="membership-title">
                  Riprenditi i tuoi diritti, partendo da una bozza pronta.
                </h2>
              </div>

              <article className="frame-card overflow-hidden p-0" data-testid="membership-image-card">
                <div className="aspect-[4/3] border-b-2 border-foreground">
                  <img
                    src="https://images.unsplash.com/photo-1564846824194-346b7871b855?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                    alt="Documento in fase di firma"
                    className="image-lift h-full w-full object-cover object-center grayscale"
                    data-testid="membership-image"
                  />
                </div>
                <div className="p-6 md:p-8">
                  <p className="eyebrow-label">Cosa ottieni subito</p>
                  <ul className="mt-4 space-y-4 text-sm leading-7 text-muted md:text-base" data-testid="membership-benefit-list">
                    <li className="border-b border-foreground/20 pb-4">Una bozza scaricabile del caso, utile per organizzare prove e dettagli.</li>
                    <li className="border-b border-foreground/20 pb-4">Una struttura semplice per descrivere l'impatto subito da te o dalla tua famiglia.</li>
                    <li>Un linguaggio chiaro, pensato per cittadini, giornalisti, attivisti e professionisti che vogliono capire subito il problema.</li>
                  </ul>
                </div>
              </article>
            </div>

            <div className="reveal-up reveal-delay-1 frame-card" data-testid="membership-form-card">
              <form className="grid gap-5" onSubmit={handleSubmit} data-testid="membership-form">
                <div className="grid gap-2">
                  <label className="eyebrow-label" htmlFor="fullName" data-testid="form-label-full-name">
                    Nome e cognome
                  </label>
                  <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    className="form-field"
                    placeholder="Es. Maria Rossi"
                    value={formData.fullName}
                    onChange={updateField}
                    required
                    data-testid="membership-full-name-input"
                  />
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <div className="grid gap-2">
                    <label className="eyebrow-label" htmlFor="email" data-testid="form-label-email">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      className="form-field"
                      placeholder="nome@email.it"
                      value={formData.email}
                      onChange={updateField}
                      required
                      data-testid="membership-email-input"
                    />
                  </div>

                  <div className="grid gap-2">
                    <label className="eyebrow-label" htmlFor="issueType" data-testid="form-label-issue-type">
                      Tipo di segnalazione
                    </label>
                    <select
                      id="issueType"
                      name="issueType"
                      className="form-field"
                      value={formData.issueType}
                      onChange={updateField}
                      required
                      data-testid="membership-issue-type-select"
                    >
                      <option value="">Seleziona una categoria</option>
                      <option value="credito">Credito / assicurazione</option>
                      <option value="lavoro">Lavoro / recruiting</option>
                      <option value="prezzi">Prezzi / e-commerce</option>
                      <option value="deepfake">Deepfake / identità</option>
                      <option value="altro">Altro caso algoritmico</option>
                    </select>
                  </div>
                </div>

                <div className="grid gap-2">
                  <label className="eyebrow-label" htmlFor="impact" data-testid="form-label-impact">
                    Impatto percepito
                  </label>
                  <select
                    id="impact"
                    name="impact"
                    className="form-field"
                    value={formData.impact}
                    onChange={updateField}
                    required
                    data-testid="membership-impact-select"
                  >
                    <option value="">Seleziona l'impatto principale</option>
                    <option value="economico">Perdita economica</option>
                    <option value="reputazionale">Danno reputazionale</option>
                    <option value="accesso-negato">Servizio negato</option>
                    <option value="discriminazione">Trattamento discriminatorio</option>
                    <option value="stress">Stress / vulnerabilità personale</option>
                  </select>
                </div>

                <div className="grid gap-2">
                  <label className="eyebrow-label" htmlFor="details" data-testid="form-label-details">
                    Racconta in breve cosa è successo
                  </label>
                  <textarea
                    id="details"
                    name="details"
                    rows="6"
                    className="form-field min-h-[160px]"
                    placeholder="Descrivi il contesto, la piattaforma coinvolta e l'effetto concreto della decisione automatizzata."
                    value={formData.details}
                    onChange={updateField}
                    required
                    data-testid="membership-details-textarea"
                  />
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4 border-t-2 border-foreground pt-5">
                  <p className="max-w-md text-sm leading-7 text-muted" data-testid="membership-form-note">
                    Al click generiamo una bozza locale scaricabile del caso, utile per ordinare le informazioni e condividerle con il tuo gruppo di tutela.
                  </p>
                  <button type="submit" className="button-primary" data-testid="membership-submit-button">
                    Genera la bozza <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </form>

              {savedDraft ? (
                <article className="mt-6 border-2 border-foreground bg-accent/50 p-5" data-testid="saved-draft-summary-card">
                  <p className="eyebrow-label">Ultima bozza salvata</p>
                  <p className="mt-3 font-display text-2xl font-bold tracking-tight" data-testid="saved-draft-summary-title">
                    Caso preparato: {savedDraft.issueType || "Segnalazione generale"}
                  </p>
                  <p className="mt-3 text-sm leading-7 text-muted" data-testid="saved-draft-summary-details">
                    Bozza creata per {savedDraft.fullName} il {new Date(savedDraft.savedAt).toLocaleDateString("it-IT")} con impatto principale “{savedDraft.impact}”.
                  </p>
                </article>
              ) : null}
            </div>
          </div>
        </section>

        <section className="section-border py-24 md:py-32" data-testid="faq-section">
          <div className="page-container grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
            <div className="reveal-up">
              <p className="section-kicker" data-testid="faq-kicker">
                FAQ
              </p>
              <h2 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="faq-title">
                Domande che arrivano quando l'algoritmo smette di sembrare neutrale.
              </h2>
            </div>

            <div className="reveal-up reveal-delay-1 frame-card" data-testid="faq-accordion-card">
              <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                {faqItems.map((item) => (
                  <AccordionItem value={item.slug} key={item.slug} className="border-b-2 border-foreground last:border-b-0" data-testid={`faq-item-${item.slug}`}>
                    <AccordionTrigger
                      className="py-6 text-left font-display text-lg font-bold tracking-tight hover:no-underline"
                      data-testid={`faq-trigger-${item.slug}`}
                    >
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 text-sm leading-7 text-muted md:text-base" data-testid={`faq-content-${item.slug}`}>
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      </main>

      <footer className="section-border bg-secondary py-16 text-secondary-foreground" data-testid="site-footer">
        <div className="page-container grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
          <div>
            <p className="section-kicker !border-secondary-foreground !bg-transparent !normal-case !tracking-tight !text-secondary-foreground" data-testid="footer-kicker">
              SviluppAi.it
            </p>
            <h2 className="max-w-3xl font-display text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="footer-title">
              Siamo un'associazione: se l'AI sbaglia, i consumatori rispondono insieme.
            </h2>
          </div>

          <div className="grid gap-5 lg:justify-items-end">
            <a href="#aderisci" className="button-primary w-full justify-center lg:w-auto" data-testid="footer-cta-button">
              Prepara la tua bozza <ChevronRight className="h-4 w-4" />
            </a>
            <p className="max-w-lg text-sm leading-7 text-secondary-foreground/80 lg:text-right" data-testid="footer-note">
              Associazione italiana nata per tutelare i consumatori dagli errori, dalle discriminazioni e dai danni causati dall'intelligenza artificiale e dai sistemi decisionali automatizzati.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}